import { AttributesList, createAttributesItem as createItem } from 'src/utils/createAttributesItem.ts';
import {
    ValvesPageAmQuery,
    ValvesPageEuQuery,
} from 'src/generated/operations.ts';
import { filterEmpty } from 'src/utils/filterEmpty.ts';
import { AttributesAliases } from 'src/generated/dict.ts';
import { MESSAGES } from 'src/modules/common/constants.ts';

export type ValveData = ValvesPageEuQuery['list']['products']['0']['product']
| ValvesPageAmQuery['list']['products']['0']['product'];

export function prepareValveAttributes(
    product: ValveData,
    convertVnom: (vnom: string) => ({ raw: string[]; localized: string[]} | null),
): [AttributesList, AttributesList] {
    const isEffectivePV2Meaningful = product.calcEffectivePV2 && product.calcEffectivePV2 !== product.calcEffectivePV;
    let attrs: [AttributesList, AttributesList] = [[], []];
    const is6way = product.pattern?.raw.join('').includes('6');
    if ('kvs' in product) {
        const clipPosKvs = product.getClipPositionKvs
            ? { raw: [product.getClipPositionKvs], localized: [product.getClipPositionKvs] } : null;
        attrs = [
            [
                [AttributesAliases.dn, product.dn],
                ...(product.getClipPosition ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [product.getClipPosition], localized: [product.getClipPosition] },
                ) : []),
                ...(product.getClipPositionPI ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [product.getClipPositionPI], localized: [product.getClipPositionPI] },
                ) : []),
                ...createItem(
                    is6way ? AttributesAliases.kvssequence1 : AttributesAliases.kvs,
                    clipPosKvs || product.kvs || product.kvssequence1,
                ),
                ...createItem(AttributesAliases.kvmax, product.kvmax),
                ...createItem(
                    AttributesAliases.vnomls,
                    convertVnom(product.vnomm3h?.raw.join('') ?? ''),
                ),
                ...createItem(AttributesAliases._511_Kvs_Sequence_2, product.kvssequence2),
                ...(product.calcEffectiveDpv ? createItem(
                    MESSAGES.EffectiveDpv,
                    { raw: [product.calcEffectiveDpv], localized: [product.calcEffectiveDpv] },
                ) : []),
                ...(is6way && product.calcEffectiveDpv6way1 ? createItem(
                    MESSAGES.EffectiveDpv1,
                    { raw: [product.calcEffectiveDpv6way1], localized: [product.calcEffectiveDpv6way1] },
                ) : []),
                ...(is6way && product.calcEffectiveDpv6way2 ? createItem(
                    MESSAGES.EffectiveDpv2,
                    { raw: [product.calcEffectiveDpv6way2], localized: [product.calcEffectiveDpv6way2] },
                ) : []),
                ...(product.calcMinReqDpv ? createItem(
                    is6way ? MESSAGES.MinReqDpv1 : MESSAGES.MinReqDpv,
                    { raw: [product.calcMinReqDpv], localized: [product.calcMinReqDpv] },
                ) : []),
                ...(is6way && product.calcMinReqDpv2 ? createItem(
                    MESSAGES.MinReqDpv2,
                    { raw: [product.calcMinReqDpv2], localized: [product.calcMinReqDpv2] },
                ) : []),
                ...(product.calcEffectivePV ? createItem(
                    MESSAGES.EffectivePV,
                    { raw: [product.calcEffectivePV], localized: [product.calcEffectivePV] },
                ) : []),
                ...(is6way && product.calcEffectivePV1 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV1,
                    { raw: [product.calcEffectivePV1], localized: [product.calcEffectivePV1] },
                ) : []),
                ...(is6way && product.calcEffectivePV2 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV2,
                    { raw: [product.calcEffectivePV2], localized: [product.calcEffectivePV2] },
                ) : []),
                [AttributesAliases.FluidTemperatureRange, product.FluidTemperatureRange],
                [AttributesAliases.pipeConnection, product.pipeConnection],
            ], [
                [AttributesAliases.PN, product.PN],
                ...(product.calcFlowVelocity ? createItem(
                    is6way ? MESSAGES.FlowVelocity1 : MESSAGES.FlowVelocity,
                    { raw: [product.calcFlowVelocity], localized: [product.calcFlowVelocity] },
                ) : []),
                ...(is6way && product.calcFlowVelocity2 ? createItem(
                    MESSAGES.FlowVelocity2,
                    { raw: [product.calcFlowVelocity2], localized: [product.calcFlowVelocity2] },
                ) : []),
                [AttributesAliases._28_Differential_pressure_deltapmaxValue, product.maxDiffPressure],
                [AttributesAliases.PermissiblePressure, product.PermissiblePressure],
                [AttributesAliases.ValveBody, product.ValveBody],
            ]];
    }
    if ('cv' in product) {
        const clipPosKvs = product.getClipPositionKvs
            ? { raw: [product.getClipPositionKvs], localized: [product.getClipPositionKvs] } : null;
        attrs = [
            [
                [AttributesAliases.bodySize, product.bodySize],
                ...(product.getClipPosition ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [product.getClipPosition], localized: [product.getClipPosition] },
                ) : []),
                ...(product.getClipPositionPI ? createItem(
                    MESSAGES.clipPosition,
                    { raw: [product.getClipPositionPI], localized: [product.getClipPositionPI] },
                ) : []),
                ...createItem(
                    is6way ? AttributesAliases.cvsequence1 : AttributesAliases.cv,
                    clipPosKvs || product.cv || product.cvsequence1,
                ),
                ...createItem(
                    AttributesAliases.vnomgpm,
                    product.vnomgpm,
                ),
                ...createItem(AttributesAliases._6969_Seq_2_Cv, product.cvsequence2),
                ...(product.calcEffectiveDpv ? createItem(
                    MESSAGES.EffectiveDpv,
                    { raw: [product.calcEffectiveDpv], localized: [product.calcEffectiveDpv] },
                ) : []),
                ...(is6way && product.calcEffectiveDpv6way1 ? createItem(
                    MESSAGES.EffectiveDpv1,
                    { raw: [product.calcEffectiveDpv6way1], localized: [product.calcEffectiveDpv6way1] },
                ) : []),
                ...(is6way && product.calcEffectiveDpv6way2 ? createItem(
                    MESSAGES.EffectiveDpv2,
                    { raw: [product.calcEffectiveDpv6way2], localized: [product.calcEffectiveDpv6way2] },
                ) : []),
                ...(product.calcMinReqDpv ? createItem(
                    is6way ? MESSAGES.MinReqDpv1 : MESSAGES.MinReqDpv,
                    { raw: [product.calcMinReqDpv], localized: [product.calcMinReqDpv] },
                ) : []),
                ...(is6way && product.calcMinReqDpv2 ? createItem(
                    MESSAGES.MinReqDpv2,
                    { raw: [product.calcMinReqDpv2], localized: [product.calcMinReqDpv2] },
                ) : []),
                ...(product.calcEffectivePV ? createItem(
                    MESSAGES.EffectivePV,
                    { raw: [product.calcEffectivePV], localized: [product.calcEffectivePV] },
                ) : []),
                ...(is6way && product.calcEffectivePV1 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV1,
                    { raw: [product.calcEffectivePV1], localized: [product.calcEffectivePV1] },
                ) : []),
                ...(is6way && product.calcEffectivePV2 && isEffectivePV2Meaningful ? createItem(
                    MESSAGES.EffectivePV2,
                    { raw: [product.calcEffectivePV2], localized: [product.calcEffectivePV2] },
                ) : []),
                [AttributesAliases._461_Fluid_Temp_Range_water, product.FluidTemperatureRange],
            ], [
                [AttributesAliases.CloseOffPressure, product.closeOffPressure],
                ...(product.calcFlowVelocity ? createItem(
                    is6way ? MESSAGES.FlowVelocity1 : MESSAGES.FlowVelocity,
                    { raw: [product.calcFlowVelocity], localized: [product.calcFlowVelocity] },
                ) : []),
                ...(is6way && product.calcFlowVelocity2 ? createItem(
                    MESSAGES.FlowVelocity2,
                    { raw: [product.calcFlowVelocity2], localized: [product.calcFlowVelocity2] },
                ) : []),
                [AttributesAliases.bodyPressureRating, product.bodyPressureRating],
                [AttributesAliases._37_Permissible_operating_pressure_ps, product.PermissiblePressure],
                [AttributesAliases.ValveBody, product.trim],
            ],
        ];
    }
    return [attrs[0].filter(filterEmpty), attrs[1].filter(filterEmpty)];
}
